import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { cn } from '~/utils/cn';

import type { CostTotalDistribution } from '../types/cost-estimate.types';
import { formatUsd } from '../utils/format-usd';

/**
 * Run total plus its likely range (low – high, ±margin, confidence). The range
 * line is omitted when the estimate predates it — never replaced by min–max,
 * which is single-product extremes × catalog size and misleads.
 */
export function CostTotalValue({
  total,
  testId = 'cost-total',
}: {
  total: CostTotalDistribution | null;
  /** E2E `data-testid` for the total; the range line is `${testId}-range`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  if (!total)
    return (
      <span data-testid={testId} className="text-lg font-semibold">
        —
      </span>
    );
  const hasRange = total.lowUsd != null && total.highUsd != null;
  const hasStats = total.rangeMargin != null && total.rangeConfidence != null;
  return (
    <>
      <span data-testid={testId} className="text-lg font-semibold">
        {formatUsd(total.avgUsd)}
      </span>
      {hasRange ? (
        <span
          data-testid={`${testId}-range`}
          className="text-muted-foreground block text-xs font-normal"
        >
          {hasStats
            ? t('costEstimate.likelyRange', {
                low: formatUsd(total.lowUsd ?? 0),
                high: formatUsd(total.highUsd ?? 0),
                margin: Math.round((total.rangeMargin ?? 0) * 100),
                confidence: Math.round((total.rangeConfidence ?? 0) * 100),
              })
            : t('costEstimate.likelyRangeShort', {
                low: formatUsd(total.lowUsd ?? 0),
                high: formatUsd(total.highUsd ?? 0),
              })}
        </span>
      ) : null}
    </>
  );
}

/**
 * Collapsed "Details" disclosure holding the simulated p99 and the theoretical
 * min–max extremes. `extra` appends host rows (e.g. the details page's calibrated
 * flag / error) and keeps the disclosure visible when there is no total.
 */
export function CostTotalDetails({
  total,
  extra,
  testId = 'cost-total-details',
}: {
  total: CostTotalDistribution | null;
  extra?: ReactNode;
  /** E2E `data-testid` for the disclosure; trigger/values derive `${testId}-...`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  if (!total && !extra) return null;
  return (
    <Collapsible data-testid={testId} open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button
          data-testid={`${testId}-toggle-button`}
          variant="ghost"
          size="sm"
          className="text-muted-foreground h-auto cursor-pointer gap-1 px-0 py-0 text-xs hover:bg-transparent"
        >
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
            aria-hidden="true"
          />
          {t('costEstimate.details')}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent data-testid={`${testId}-content`}>
        <dl className="text-muted-foreground mt-2 flex flex-col gap-1 text-xs">
          {total ? (
            <>
              <div className="flex flex-wrap gap-x-2">
                <dt>{t('costEstimate.simulatedP99')}</dt>
                <dd data-testid={`${testId}-p99-value`} className="text-foreground tabular-nums">
                  {formatUsd(total.p99Usd)}
                </dd>
              </div>
              <div className="flex flex-wrap gap-x-2">
                <dt>{t('costEstimate.theoreticalExtremes')}</dt>
                <dd
                  data-testid={`${testId}-extremes-value`}
                  className="text-foreground tabular-nums"
                >
                  {formatUsd(total.minUsd)} – {formatUsd(total.maxUsd)}
                </dd>
              </div>
            </>
          ) : null}
          {extra}
        </dl>
      </CollapsibleContent>
    </Collapsible>
  );
}
